import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmBox from '../../../components/FlowBuilder/Common/ConfirmBox/index.tsx';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { Button, TablePagination } from '../../../components/ui';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';
import { dateFormate } from '../../../utils/helper.jsx';
import TemplateColumnHeader from '../components/list/TemplateColumnHeader';
import TemplateDataTable from '../components/shared/TemplateDataTable';
import TemplateSearchableSelect from '../components/shared/TemplateSearchableSelect';
import TemplateQueueRowActions from '../components/queue/TemplateQueueRowActions';
import StatusChip from '../components/ui/StatusChip';
import useTemplateSx from '../hooks/useTemplateSx';
import { templateService } from '../services/templateService';
import { TEMPLATE_BASE_PATH, TEMPLATE_LIST_PAGE_SIZE } from '../constants/templateApi';

const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  processing: 'Processing',
  sent: 'Sent',
  failed: 'Failed',
  scheduled: 'Scheduled',
  queued: 'Queued',
  running: 'Running',
  done: 'Done',
  paused: 'Paused',
};

const STATUS_FILTERS = [
  { label: 'All statuses', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Pending', value: 'pending' },
  { label: 'Processing', value: 'processing' },
  { label: 'Sent', value: 'sent' },
  { label: 'Failed', value: 'failed' },
  { label: 'Paused', value: 'paused' },
];

function actionFailure(error) {
  const data = error?.response?.data?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.general_error === 'string') return data.general_error;
  return 'Could not update the queue';
}

function readPayload(res) {
  if (res?.status !== 200 || res?.data?.status === false) {
    const message = res?.data?.general_error || res?.data?.data?.general_error || res?.data?.data || 'Failed to load queue items';
    return { error: typeof message === 'string' ? message : 'Failed to load queue items' };
  }
  const body = res?.data ?? {};
  const payload = body.results || body.total_count != null ? body : (body.data ?? {});
  return {
    results: payload.results ?? [],
    total: payload.total_count ?? 0,
    job: payload.job ?? null,
    error: null,
  };
}

function SummaryField({ label, value, secondary }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
      {secondary ? (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {secondary}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function TemplateQueueItemsPage() {
  return (
    <FeatureThemeProvider>
      <TemplateQueueItemsPageContent />
    </FeatureThemeProvider>
  );
}

function TemplateQueueItemsPageContent() {
  const styles = useTemplateSx();
  const navigate = useNavigate();
  const { jobId } = useParams();
  const location = useLocation();
  const seededJob = location.state?.job ?? null;
  const [loadedJob, setLoadedJob] = useState(seededJob);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(TEMPLATE_LIST_PAGE_SIZE);
  const [destination, setDestination] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTERS[0]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const job = loadedJob || seededJob;

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    templateService.getTemplateQueueItems({
      jobId,
      page,
      limit: pageSize,
      destination: destination.trim(),
      queueStatus: statusFilter?.value || '',
    }).then((res) => {
      if (cancelled) return;
      const payload = readPayload(res);
      setItems(payload.results || []);
      setTotal(payload.total || 0);
      setError(payload.error);
      if (payload.job) setLoadedJob(payload.job);
    }).catch(() => {
      if (cancelled) return;
      setItems([]);
      setTotal(0);
      setError('Failed to load queue items');
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [jobId, page, pageSize, destination, statusFilter, reloadKey]);

  const renderHeader = (label) => () => <TemplateColumnHeader>{label}</TemplateColumnHeader>;

  const columns = useMemo(() => [
    {
      accessorKey: 'destination',
      header: 'Destination',
      Header: renderHeader('Destination'),
      enableSorting: false,
      size: 180,
    },
    {
      accessorKey: 'queue_status',
      header: 'Status',
      Header: renderHeader('Status'),
      enableSorting: false,
      size: 120,
      Cell: ({ cell }) => (
        <StatusChip
          value={cell.getValue()}
          label={STATUS_LABELS[cell.getValue()] || cell.getValue()}
        />
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created at',
      Header: renderHeader('Created at'),
      enableSorting: false,
      size: 180,
      Cell: ({ cell }) => (
        cell.getValue() ? dateFormate(cell.getValue(), 'DD MMM YYYY, hh:mm A') : '—'
      ),
    },
    {
      accessorKey: 'scheduled_at',
      header: 'Scheduled at',
      Header: renderHeader('Scheduled at'),
      enableSorting: false,
      size: 180,
      Cell: ({ cell }) => (
        cell.getValue() ? dateFormate(cell.getValue(), 'DD MMM YYYY, hh:mm A') : '—'
      ),
    },
    {
      accessorKey: 'last_error',
      header: 'Error',
      Header: renderHeader('Error'),
      enableSorting: false,
      size: 240,
      Cell: ({ cell }) => cell.getValue() || '—',
    },
    {
      accessorKey: 'actions',
      header: 'Action',
      Header: renderHeader('Action'),
      enableSorting: false,
      size: 72,
      minSize: 72,
      maxSize: 72,
      grow: false,
      muiTableHeadCellProps: { sx: styles.actionColumnHeadCell },
      muiTableBodyCellProps: { sx: styles.actionColumnBodyCell },
      Cell: ({ row }) => (
        row.original.queue_status === 'failed' ? (
          <TemplateQueueRowActions
            row={row.original}
            onRetryItem={() => setPendingAction({ type: 'item', row: row.original })}
          />
        ) : null
      ),
    },
  ], [styles.actionColumnBodyCell, styles.actionColumnHeadCell]);

  const title = job?.template_name || 'Queue destinations';

  const runPendingAction = async (confirmed) => {
    const action = pendingAction;
    setPendingAction(null);
    if (!confirmed || !action) return;
    setActionError(null);
    try {
      const res = action.type === 'item'
        ? await templateService.retryTemplateQueueItem(action.row.item_id)
        : await templateService.retryFailedTemplateQueue(jobId);
      if (res?.status !== 200 || res?.data?.status === false) {
        setActionError(actionFailure({ response: res }));
        return;
      }
      setReloadKey((value) => value + 1);
    } catch (err) {
      setActionError(actionFailure(err));
    }
  };

  return (
    <Box sx={{ ...styles.pageRoot, minHeight: 0 }}>
      <DynamicBreadcrumbs
        items={[
          { label: <IconButton color="primary"><HomeIcon /></IconButton>, href: '/' },
          { label: 'Template Queue', href: `${TEMPLATE_BASE_PATH}/template-queue` },
          { label: title },
        ]}
        onBreadcrumbClick={(_event, item) => {
          if (item?.href) navigate(item.href);
        }}
      />

      <Box sx={{ ...styles.pageHeader, mt: { xs: 1.8, sm: 1.2 } }}>
        <Box>
          <Typography variant="h5" sx={styles.pageTitle}>
            {title}
          </Typography>
          <Typography variant="body2" sx={styles.pageSubtitle}>
            Destinations for this send
          </Typography>
        </Box>
        {job?.queue_status ? (
          <StatusChip
            value={job.queue_status}
            label={STATUS_LABELS[job.queue_status] || job.queue_status}
          />
        ) : null}
      </Box>

      <Box sx={styles.dashboardSurface}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            px: { xs: 1, sm: 1.5 },
            pt: 1.5,
            pb: 0.5,
          }}
        >
          <SummaryField
            label="Organisation"
            value={job?.organisation_name || job?.company_id}
            secondary={job?.bot_number}
          />
          <SummaryField
            label="Shared by"
            value={job?.created_by}
            secondary={job?.created_at ? dateFormate(job.created_at, 'DD MMM YYYY, hh:mm A') : ''}
          />
        </Box>

        <Box sx={styles.filtersToolbar}>
          <Box sx={styles.filtersBar}>
            <TextField
              size="small"
              placeholder="Search destination"
              value={destination}
              onChange={(event) => {
                const value = event.target.value.replace(/^\s+/, '').replace(/\s{2,}/g, ' ');
                setDestination(value);
                setPage(0);
              }}
              sx={{ ...styles.filterSearch, width: { xs: '100%', sm: 280 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ width: { xs: '100%', sm: 220 } }}>
              <TemplateSearchableSelect
                variant="filter"
                label=""
                placeholder="All statuses"
                options={STATUS_FILTERS}
                value={statusFilter}
                onChange={(option) => {
                  setStatusFilter(option || STATUS_FILTERS[0]);
                  setPage(0);
                }}
                getOptionLabel={(option) => option?.label ?? ''}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
              />
            </Box>
            {Number(job?.failed) > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingAction({ type: 'job', row: job })}
              >
                Retry failed
              </Button>
            ) : null}
          </Box>
        </Box>

        {error ? (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        ) : null}
        {actionError ? (
          <Typography color="error" sx={{ mb: 2 }}>{actionError}</Typography>
        ) : null}

        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
          <TemplateDataTable
            columns={columns}
            data={items}
            getRowId={(row, index) => `${row.destination}-${row.created_at}-${index}`}
            layoutMode="grid"
            enableColumnPinning
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableBottomToolbar={false}
            enablePagination={false}
            initialState={{ density: 'compact' }}
            state={{
              isLoading,
              columnPinning: { right: ['actions'] },
            }}
          />
        </Box>

        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={total}
          onPageChange={setPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(Number(nextPageSize) || TEMPLATE_LIST_PAGE_SIZE);
            setPage(0);
          }}
          sx={{ mt: 2 }}
        />
      </Box>
      <ConfirmBox
        open={Boolean(pendingAction)}
        onClose={runPendingAction}
        options={{
          title: pendingAction?.type === 'item' ? 'Retry this number' : 'Retry failed rows',
          text: pendingAction?.type === 'item'
            ? (job?.queue_status === 'paused'
              ? `${pendingAction?.row?.destination || 'This number'} stays held until you resume this send.`
              : `${pendingAction?.row?.destination || 'This number'} will be sent again. Sent rows stay sent.`)
            : (job?.queue_status === 'paused'
              ? `${job?.failed || 0} failed rows stay held until you resume. Sent rows stay sent.`
              : `${job?.failed || 0} failed rows will be sent again. Sent rows stay sent.`),
          confirmButtonText: 'Retry',
          cancelButtonText: 'Cancel',
        }}
      />
    </Box>
  );
}
